import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronDownIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  TableCellsIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/20/solid';
import Pagination from '../components/Pagination';
import Reveal from '../components/Reveal';
import { TableSkeleton } from '../components/Skeletons';
import useProducts from '../hooks/useProducts';
import useDebouncedValue from '../hooks/useDebouncedValue';

const PAGE_SIZES = [10, 25, 50];

function formatPrice(value) {
  return (Number(value) || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
}

function StockBadge({ stock }) {
  const tone =
    stock === 0
      ? 'bg-rose-50 text-rose-600 ring-rose-100'
      : stock < 20
        ? 'bg-amber-50 text-amber-700 ring-amber-100'
        : 'bg-emerald-50 text-emerald-700 ring-emerald-100';

  return (
    <span className={`chip ring-1 ring-inset ${tone}`}>
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          stock === 0 ? 'bg-rose-500' : stock < 20 ? 'bg-amber-500' : 'bg-emerald-500'
        }`}
      />
      {stock === 0 ? 'Habis' : `${stock} pcs`}
    </span>
  );
}

export default function List() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZES[0]);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 400);
  const topRef = useRef(null);

  const { products, total, loading, error, reload } = useProducts({ page, limit, search });
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    setPage(1);
  }, [search, limit]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const scrollToTop = () => {
    const element = topRef.current;
    if (!element) return;
    const top = element.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    scrollToTop();
  };

  const clearSearch = () => {
    setSearchInput('');
    setPage(1);
  };

  const isSearching = search.trim().length > 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <section className="relative overflow-hidden border-b border-slate-200/70 bg-white">
        <div className="pointer-events-none absolute inset-0 bg-soft-gradient" aria-hidden="true" />
        <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 animate-blob rounded-full bg-sky-200/50 blur-3xl" aria-hidden="true" />

        <div ref={topRef} className="container-page relative py-10 sm:py-14">
          <Reveal>
            <span className="chip border border-sky-200/70 bg-sky-50 text-sky-700">
              <TableCellsIcon className="h-3.5 w-3.5" />
              Tabel data
            </span>
            <h1 className="mt-4 text-3xl font-extrabold text-slate-900 sm:text-4xl">
              Data produk lengkap
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
              {isSearching ? (
                <>
                  Menampilkan hasil untuk{' '}
                  <span className="font-semibold text-slate-900">“{search}”</span>
                </>
              ) : (
                'Lihat id, judul, harga, stok, dan rating dalam satu tabel yang rapi.'
              )}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-page py-8 sm:py-10">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-md">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Cari produk di tabel…"
              aria-label="Cari produk"
              className="input-base !py-3 pl-11 pr-11"
            />
            {searchInput && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label="Hapus pencarian"
                className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              >
                <XMarkIcon className="h-4 w-4" strokeWidth={2.5} />
              </button>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 sm:justify-end">
            <span className="text-sm text-slate-500">
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-2 w-2 animate-ping rounded-full bg-sky-500" />
                  Memuat…
                </span>
              ) : (
                <>
                  <span className="font-semibold text-slate-800">{total}</span> baris
                </>
              )}
            </span>

            <label className="relative inline-flex items-center">
              <span className="sr-only">Baris per halaman</span>
              <select
                value={limit}
                onChange={(event) => setLimit(Number(event.target.value))}
                className="input-base appearance-none !py-2.5 pl-3.5 pr-9 text-sm font-semibold"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size} / halaman
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-3 h-4 w-4 text-slate-400" />
            </label>
          </div>
        </div>

        {error ? (
          <div className="card-surface flex flex-col items-center justify-center gap-4 px-6 py-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-500">
              <ExclamationTriangleIcon className="h-7 w-7" />
            </span>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Gagal memuat data</h2>
              <p className="mt-1 max-w-sm text-sm text-slate-600">{error}</p>
            </div>
            <button type="button" onClick={reload} className="btn-primary">
              Coba lagi
            </button>
          </div>
        ) : loading && products.length === 0 ? (
          <div className="card-surface overflow-hidden">
            <TableSkeleton rows={limit} />
          </div>
        ) : products.length === 0 ? (
          <div className="card-surface flex flex-col items-center justify-center gap-4 px-6 py-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <TableCellsIcon className="h-7 w-7" />
            </span>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Tidak ada data</h2>
              <p className="mt-1 max-w-sm text-sm text-slate-600">
                {isSearching
                  ? `Tidak ada hasil untuk “${search}”. Coba kata kunci lain.`
                  : 'Belum ada produk yang bisa ditampilkan.'}
              </p>
            </div>
            {isSearching && (
              <button type="button" onClick={clearSearch} className="btn-secondary">
                Hapus pencarian
              </button>
            )}
          </div>
        ) : (
          <>
            <div className={`transition-opacity duration-300 ${loading ? 'pointer-events-none opacity-50' : ''}`}>
            {/* Desktop table */}
            <div className="card-surface hidden overflow-hidden md:block">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] table-auto text-left">
                  <thead>
                    <tr className="border-b border-slate-200/80 bg-slate-50/80">
                      {['Produk', 'Kategori', 'Harga', 'Stok', 'Rating', ''].map((head) => (
                        <th
                          key={head}
                          scope="col"
                          className="px-5 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500"
                        >
                          {head}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {products.map((product) => (
                      <tr
                        key={product.id}
                        className="group transition-colors hover:bg-brand-50/40"
                      >
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3.5">
                            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl border border-slate-200/70 bg-slate-100">
                              <img
                                src={product.thumbnail}
                                alt={product.title}
                                loading="lazy"
                                className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="max-w-[280px] truncate text-sm font-semibold text-slate-800 group-hover:text-brand-700">
                                {product.title}
                              </p>
                              <p className="mt-0.5 text-xs text-slate-400">
                                #{product.id}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="chip bg-slate-100 capitalize text-slate-600">
                            {String(product.category || '-').replaceAll('-', ' ')}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-sm font-bold text-slate-900">
                            {formatPrice(product.price)}
                          </span>
                          {product.discountPercentage > 0 && (
                            <span className="ml-2 text-[11px] font-semibold text-rose-500">
                              -{Math.round(product.discountPercentage)}%
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          <StockBadge stock={product.stock} />
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-700">
                            <StarSolid className="h-4 w-4 text-amber-400" />
                            {Number(product.rating).toFixed(1)}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <NavLink
                            to={`/detail/${product.id}`}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700"
                          >
                            Detail
                            <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
                          </NavLink>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile cards */}
            <ul className="space-y-3 md:hidden">
              {products.map((product) => (
                <li key={product.id}>
                  <NavLink
                    to={`/detail/${product.id}`}
                    className="group flex gap-3.5 rounded-2xl border border-slate-200/70 bg-white p-3.5 shadow-soft transition active:scale-[0.99]"
                  >
                    <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                      <img
                        src={product.thumbnail}
                        alt={product.title}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="line-clamp-2 text-sm font-semibold text-slate-800">
                          {product.title}
                        </p>
                        <ArrowRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-brand-600" />
                      </div>
                      <p className="mt-0.5 text-[11px] capitalize text-slate-400">
                        #{product.id} · {String(product.category || '-').replaceAll('-', ' ')}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="text-sm font-bold text-slate-900">
                          {formatPrice(product.price)}
                        </span>
                        <StockBadge stock={product.stock} />
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600">
                          <StarIcon className="h-3.5 w-3.5 text-amber-400" />
                          {Number(product.rating).toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </NavLink>
                </li>
              ))}
            </ul>
            </div>

            <div className="mt-10">
              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={total}
                limit={limit}
                onChange={handlePageChange}
                loading={loading}
                itemLabel="baris"
              />
            </div>
          </>
        )}
      </section>
    </div>
  );
}
